'use client';

import { usePathname, useRouter } from 'next/navigation';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { LoadingSpinner } from "@/components/shared";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const router = useRouter();
  const pathname = usePathname();

  const checkAuthentication = useCallback(async () => {
    const accessToken = localStorage.getItem('accessToken');
    const refreshToken = localStorage.getItem('refreshToken');

    if (!accessToken) {
      setUser(null);
      setIsAuthenticated(false);
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${process.env.NEXT_PUBLIC_API_BASE_URL}/api/me`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      if (response.ok) {
        const data = await response.json();
        setUser(data);
        setIsAuthenticated(true);
        setLoading(false);
        return;
      }

      if (response.status === 401 && refreshToken) {
        const refreshResponse = await fetch(`${process.env.NEXT_PUBLIC_API_BASE_URL}/api/token/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refresh_token: refreshToken }),
        });

        if (refreshResponse.ok) {
          const refreshData = await refreshResponse.json();
          localStorage.setItem('accessToken', refreshData.token);

          const retryResponse = await fetch(`${process.env.NEXT_PUBLIC_API_BASE_URL}/api/me`, {
            headers: { Authorization: `Bearer ${refreshData.token}` },
          });

          if (retryResponse.ok) {
            const retryData = await retryResponse.json();
            setUser(retryData);
            setIsAuthenticated(true);
            setLoading(false);
            return;
          }
        }
      }

      // All attempts failed
      setUser(null);
      setIsAuthenticated(false);
    } catch (error) {
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  }, []);

  // Run auth check on mount
  useEffect(() => {
    checkAuthentication();
  }, [checkAuthentication]);

  // refreshAuth: callable after 2FA success to re-validate token without page reload
  // Does NOT set loading to true — prevents unmounting the calling component
  // Retries once on failure with a small delay
  const refreshAuth = useCallback(async () => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) return false;

    const tryFetch = async (token) => {
      const response = await fetch(`${process.env.NEXT_PUBLIC_API_BASE_URL}/api/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setUser(data);
        setIsAuthenticated(true);
        return true;
      }
      return false;
    };

    try {
      // First attempt
      const success = await tryFetch(accessToken);
      if (success) return true;

      // Wait briefly and retry (handles edge case where token isn't immediately valid)
      await new Promise(resolve => setTimeout(resolve, 500));
      const retryToken = localStorage.getItem('accessToken');
      if (retryToken) {
        return await tryFetch(retryToken);
      }
      return false;
    } catch (error) {
      return false;
    }
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    setUser(null);
    setIsAuthenticated(false);
    router.replace('/login');
  }, [router]);

  const handleSessionExpired = useCallback(() => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    setUser(null);
    setIsAuthenticated(false);
    router.replace('/login?expired=true');
  }, [router]);

  // Memoïseer de context-value zodat consumers (useAuth zit in vrijwel elke
  // RouteView) niet her-renderen bij elke AuthProvider-render. Zonder dit gaf
  // elke render een nieuw value-object -> app-brede re-renders.
  const value = useMemo(
    () => ({ user, setUser, isAuthenticated, loading, logout, handleSessionExpired, refreshAuth }),
    [user, isAuthenticated, loading, logout, handleSessionExpired, refreshAuth]
  );

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
